import React, { useEffect, useState } from 'react'
import { ChevronDown, Loader2, X } from 'lucide-react'
import { toast } from 'react-toastify'

interface WorkInfoModalProps {
  isOpen: boolean
  onClose: () => void
  initialWorkInfo?: {
    employeeId?: string
    projectName?: string
    branch?: string
    shift?: string
    reference?: string
  }
  onSave: (payload: {
    employeeId: string
    projectName: string
    branch: string
    shift: string
    reference?: string
  }) => Promise<void>
}

const WorkInfoModal: React.FC<WorkInfoModalProps> = ({ isOpen, onClose, initialWorkInfo, onSave }) => {
  const [employeeId, setEmployeeId] = useState('')
  const [projectName, setProjectName] = useState('')
  const [branch, setBranch] = useState('')
  const [shift, setShift] = useState('')
  const [reference, setReference] = useState('')
  const [isSaving, setIsSaving] = useState(false)

  useEffect(() => {
    if (!isOpen) {
      return
    }

    setEmployeeId(initialWorkInfo?.employeeId || '')
    setProjectName(initialWorkInfo?.projectName || '')
    setBranch(initialWorkInfo?.branch || '')
    setShift(initialWorkInfo?.shift || '')
    setReference(initialWorkInfo?.reference || '')
  }, [initialWorkInfo, isOpen])

  if (!isOpen) return null

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()

    if (!employeeId.trim() || !projectName.trim() || !branch.trim() || !shift.trim()) {
      toast.error('Employee ID, project name, branch, and shift are required')
      return
    }

    setIsSaving(true)
    try {
      await onSave({
        employeeId: employeeId.trim(),
        projectName: projectName.trim(),
        branch: branch.trim(),
        shift: shift.trim(),
        ...(reference.trim() ? { reference: reference.trim() } : {}),
      })
      onClose()
    } catch (error: any) {
      toast.error(error?.data?.message || error?.message || 'Failed to save work info')
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 z-[70] flex items-start justify-center overflow-y-auto bg-black/50 p-3 sm:items-center sm:p-4">
      <div className="my-4 flex max-h-[calc(100vh-2rem)] w-full max-w-xl flex-col overflow-hidden rounded-lg bg-white shadow-2xl sm:my-0">
        <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3 sm:px-6 sm:py-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Update Work Info</h2>
            <p className="text-sm text-gray-500">Complete work information before selecting the applicant</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-gray-200 bg-white p-2 text-gray-500 transition hover:bg-gray-100"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5 overflow-y-auto px-4 py-4 sm:px-6 sm:py-5">
          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700">Employee ID</label>
            <input
              type="text"
              value={employeeId}
              onChange={(e) => setEmployeeId(e.target.value)}
              className="admin-filter-input py-3"
              placeholder="Enter employee ID"
            />
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700">Project Name</label>
            <input
              type="text"
              value={projectName}
              onChange={(e) => setProjectName(e.target.value)}
              className="admin-filter-input py-3"
              placeholder="Enter project name"
            />
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">Branch</label>
              <input
                type="text"
                value={branch}
                onChange={(e) => setBranch(e.target.value)}
                className="admin-filter-input py-3"
                placeholder="Enter branch"
              />
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">Shift</label>
              <div className="relative">
                <select
                  value={shift}
                  onChange={(e) => setShift(e.target.value)}
                  className="admin-filter-select py-3"
                >
                  <option value="">Select shift</option>
                  <option value="morning">Morning</option>
                  <option value="evening">Evening</option>
                  <option value="night">Night</option>
                </select>
                <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500" />
              </div>
            </div>
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700">Reference</label>
            <input
              type="text"
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              className="admin-filter-input py-3"
              placeholder="Enter reference"
            />
          </div>

          <div className="flex justify-end gap-3 border-t border-gray-200 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSaving}
              className="inline-flex items-center gap-2 rounded-md bg-[#c31200] px-4 py-2 text-sm font-medium text-white transition hover:bg-[#a71000] disabled:cursor-not-allowed disabled:opacity-60"
            >
              {isSaving && <Loader2 className="h-4 w-4 animate-spin" />}
              <span>Save Work Info</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default WorkInfoModal
